import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Empty, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import classNames from 'classnames';
import type { EChartsInstance } from 'echarts-for-react';
import ReactECharts from 'echarts-for-react';

import { useHasVerticalScrollbar } from '@/shared/hooks';
import { highlightText, matchesKeywordFilter } from '@/shared/lib/text';
import { exportContentToPdf } from '@/shared/lib/utils/exportPdf';
import { formatMetricValue } from '@/shared/lib/utils/number';
import { EllipsisText } from '@/shared/ui/EllipsisText';

import type { ExecutionResultModalMetric } from '@/entities/run';

import { ExperimentAssetTabShell } from '@/widgets/experiment-asset-detail/ExperimentAssetTabShell';

import styles from './MetricsTab.module.css';

type MetricsTabProps = {
  metrics: Array<{ key: string; value: number | string; timestamp?: number; step?: number }>;
  shellVariant?: 'panel' | 'sidebar';
  chartMetrics?: ExecutionResultModalMetric[];
  runName?: string;
};

type MetricsTabBodyProps = {
  metrics: Array<{ key: string; value: number | string; timestamp?: number; step?: number }>;
  searchQuery: string;
  shellVariant: 'panel' | 'sidebar';
};

type MetricPreparedRow = {
  metric: MetricsTabBodyProps['metrics'][number];
  displayValue: string;
  valueString: string;
};

const MetricsTabBody: React.FC<MetricsTabBodyProps> = ({ metrics, searchQuery, shellVariant }) => {
  const metricsPrepared = useMemo<MetricPreparedRow[]>(
    () =>
      metrics.map((metric) => {
        const displayValue = typeof metric.value === 'number' ? formatMetricValue(metric.value) : String(metric.value);
        return { metric, displayValue, valueString: String(metric.value) };
      }),
    [metrics]
  );

  const filteredMetrics = useMemo(
    () =>
      metricsPrepared.filter(({ metric, displayValue, valueString }) =>
        matchesKeywordFilter(searchQuery, metric.key, valueString, displayValue)
      ),
    [metricsPrepared, searchQuery]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const { hasVerticalScroll, scrollbarWidth } = useHasVerticalScrollbar(scrollRef, [filteredMetrics]);
  const useScrollGutter = shellVariant === 'panel' && hasVerticalScroll;

  const infoSectionStyle = (useScrollGutter ? { '--scrollbar-width': `${scrollbarWidth}px` } : undefined) as
    | React.CSSProperties
    | undefined;

  return (
    <div className={classNames(styles.metricsTabInner, shellVariant === 'sidebar' && styles.metricsTabInnerSidebar)}>
      {filteredMetrics.length > 0 ? (
        <div className={styles.infoSection} style={infoSectionStyle}>
          <div
            className={classNames(styles.infoHeader, {
              [styles.scrollGutterRight]: useScrollGutter,
            })}>
            <span className={styles.infoHeaderLabel}>Metric</span>
            <span className={styles.infoHeaderLabel}>Value</span>
          </div>
          <div
            ref={scrollRef}
            className={classNames(styles.infoRowsScroll, {
              [styles.scrollGutterRight]: useScrollGutter,
            })}>
            {filteredMetrics.map(({ metric, displayValue }) => (
              <div key={metric.key} className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  <EllipsisText text={metric.key} tooltipPlacement="topLeft">
                    {highlightText(metric.key, searchQuery, { className: 'highlight' })}
                  </EllipsisText>
                </span>
                <span className={styles.infoValue}>
                  <EllipsisText text={displayValue} tooltipPlacement="topRight">
                    {highlightText(displayValue, searchQuery, { className: 'highlight' })}
                  </EllipsisText>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="검색 결과가 없습니다" />
      )}
    </div>
  );
};

const MetricsChartView: React.FC<{
  chartMetrics: ExecutionResultModalMetric[];
  runName?: string;
  contentRef: React.RefObject<HTMLDivElement>;
}> = ({ chartMetrics, contentRef }) => {
  const chartRefs = useRef<Array<EChartsInstance | null>>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const metricsWithHistory = useMemo(
    () => chartMetrics.filter((m) => m.history && m.history.length > 0),
    [chartMetrics]
  );

  const lineColor = useMemo(
    () =>
      getComputedStyle(document.documentElement)
        .getPropertyValue('--opds-sem-color-container-standard-level4')
        .trim() || '#1890ff',
    []
  );

  useEffect(() => {
    const resizeCharts = () => {
      chartRefs.current.forEach((chartInstance) => {
        if (chartInstance) {
          chartInstance.resize();
        }
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      resizeCharts();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const timer = setTimeout(() => {
      resizeCharts();
    }, 100);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [metricsWithHistory]);

  const metricColumns = useMemo<ColumnsType<ExecutionResultModalMetric>>(
    () => [
      {
        title: 'Metric',
        dataIndex: 'name',
        key: 'name',
        width: '50%',
        render: (name: string) => (
          <EllipsisText text={name} tooltipPlacement="topLeft">
            {name}
          </EllipsisText>
        ),
      },
      {
        title: 'Value',
        dataIndex: 'currentValue',
        key: 'value',
        align: 'right',
        width: '50%',
        render: (value: number) => formatMetricValue(value),
      },
    ],
    []
  );

  const getChartOption = (metric: ExecutionResultModalMetric) => ({
    title: {
      text: metric.name,
      textStyle: {
        fontSize: 14,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      appendToBody: true,
      confine: false,
      extraCssText: 'max-width: min(360px, 90vw); z-index: 11000; overflow-wrap: anywhere;',
      formatter: (params: unknown) => {
        const paramsArray = params as Array<{ axisValue: number; value: number }>;
        const point = paramsArray[0];
        return `Step ${point.axisValue}<br/>${metric.name}: ${formatMetricValue(point.value)}`;
      },
    },
    xAxis: {
      type: 'category',
      data: metric.history.map((h) => h.step),
      name: 'Step',
    },
    yAxis: {
      type: 'value',
    },
    series: [
      {
        data: metric.history.map((h) => h.value),
        type: 'line',
        smooth: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: {
          color: '#000000',
        },
        lineStyle: {
          color: lineColor,
          width: 2,
        },
      },
    ],
    grid: {
      left: '8%',
      right: '5%',
      bottom: '15%',
      top: '20%',
    },
  });

  return (
    <div className={styles.chartViewContainer} ref={contentRef}>
      {metricsWithHistory.length > 0 && (
        <div
          ref={containerRef}
          className={styles.chartsGrid}
          style={{
            gridTemplateColumns:
              metricsWithHistory.length === 1
                ? '1fr'
                : metricsWithHistory.length === 2
                  ? 'repeat(2, 1fr)'
                  : 'repeat(3, 1fr)',
          }}>
          {metricsWithHistory.map((metric, index) => (
            <div key={metric.name} className={styles.chartCard}>
              <ReactECharts
                ref={(instance) => {
                  if (instance) {
                    chartRefs.current[index] = instance.getEchartsInstance();
                  }
                }}
                option={getChartOption(metric)}
                style={{ height: '280px', width: '100%' }}
                opts={{ renderer: 'canvas' }}
              />
            </div>
          ))}
        </div>
      )}

      <div className={styles.tableSection}>
        <Table
          columns={metricColumns}
          dataSource={chartMetrics}
          rowKey="name"
          pagination={false}
          size="small"
          scroll={{ y: 240 }}
        />
      </div>
    </div>
  );
};

export const MetricsTab: React.FC<MetricsTabProps> = ({ metrics, shellVariant = 'panel', chartMetrics, runName }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExportReport = useCallback(async () => {
    await exportContentToPdf({
      contentRef,
      fileName: runName || 'metrics',
      setIsExporting,
    });
  }, [runName]);

  const hasHistoryData = chartMetrics && chartMetrics.some((m) => m.history && m.history.length > 0);

  if (chartMetrics && chartMetrics.length > 0 && hasHistoryData) {
    return (
      <ExperimentAssetTabShell
        variant={shellVariant}
        onDownload={handleExportReport}
        downloadDisabled={isExporting}
        downloadButtonText="내보내기"
        downloadButtonLoading={isExporting}
        allowVerticalScroll>
        {() => <MetricsChartView chartMetrics={chartMetrics} runName={runName} contentRef={contentRef} />}
      </ExperimentAssetTabShell>
    );
  }

  return (
    <ExperimentAssetTabShell variant={shellVariant} allowVerticalScroll>
      {({ searchQuery }) => <MetricsTabBody metrics={metrics} searchQuery={searchQuery} shellVariant={shellVariant} />}
    </ExperimentAssetTabShell>
  );
};
